/**
 * Token Refresh Service
 * Proactive token refresh to prevent WebSocket disconnections
 */

import { jwtDecode } from 'jwt-decode';
import { tokenManager, apiClient } from './api.client';
import type { ApiResponse } from '@/types/api.types';

interface JWTPayload {
  userId: string;
  email: string;
  role: string;
  iat: number;
  exp: number;
}

// Token refresh events
export const TOKEN_EVENTS = {
  REFRESHED: 'token:refreshed',
  EXPIRED: 'token:expired',
  ERROR: 'token:error',
} as const;

class TokenRefreshService {
  private refreshInterval: ReturnType<typeof setInterval> | null = null;
  private readonly CHECK_INTERVAL = 4 * 60 * 1000; // 4 minutes
  private readonly REFRESH_THRESHOLD = 5 * 60 * 1000; // Refresh if <5 minutes remaining

  /**
   * Start automatic token refresh
   */
  start(): void {
    if (this.refreshInterval) {
      console.log('[TokenRefresh] Already running');
      return;
    }

    console.log('[TokenRefresh] Starting automatic token refresh');

    // Check immediately
    this.checkAndRefreshToken();

    // Check every 4 minutes
    this.refreshInterval = setInterval(() => {
      this.checkAndRefreshToken();
    }, this.CHECK_INTERVAL);
  }

  /**
   * Stop automatic token refresh
   */
  stop(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      console.log('[TokenRefresh] Stopped');
    }
  }

  /**
   * Check token expiry and refresh if needed
   */
  private async checkAndRefreshToken(): Promise<void> {
    const accessToken = tokenManager.getAccessToken();

    if (!accessToken) {
      console.log('[TokenRefresh] No access token found');
      return;
    }

    try {
      // Decode JWT to check expiry
      const decoded = jwtDecode<JWTPayload>(accessToken);
      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeRemaining = expiresAt - now;

      console.log('[TokenRefresh] Token check:', {
        expiresAt: new Date(expiresAt).toISOString(),
        timeRemaining: Math.floor(timeRemaining / 1000) + 's',
        willRefresh: timeRemaining < this.REFRESH_THRESHOLD,
      });

      // Refresh if less than 5 minutes remaining
      if (timeRemaining < this.REFRESH_THRESHOLD) {
        await this.refreshToken();
      }
    } catch (error) {
      console.error('[TokenRefresh] Token decode error:', error);
      this.emitEvent(TOKEN_EVENTS.ERROR, error);
    }
  }

  /**
   * Refresh the access token.
   * The refresh token is carried by the httpOnly 'rt' cookie (audit #17), so
   * nothing is read from client-side storage or sent in the request body.
   */
  private async refreshToken(): Promise<void> {
    try {
      console.log('[TokenRefresh] Refreshing token...');

      const response = await apiClient.post<ApiResponse<{ accessToken: string }>>(
        '/auth/refresh',
        {},
        { withCredentials: true }
      );

      if (response.data.data) {
        const { accessToken } = response.data.data;

        // Update the access token
        tokenManager.setAccessToken(accessToken);

        console.log('[TokenRefresh] Token refreshed successfully');

        // Emit refresh event for WebSocket reconnection
        this.emitEvent(TOKEN_EVENTS.REFRESHED, { accessToken });
      }
    } catch (error) {
      console.error('[TokenRefresh] Token refresh failed:', error);

      // Check if it's a refresh token expiry (7 days)
      if (error instanceof Error && error.message.includes('expired')) {
        console.log('[TokenRefresh] Refresh token expired, logout required');
        this.emitEvent(TOKEN_EVENTS.EXPIRED);
        this.stop();
      } else {
        this.emitEvent(TOKEN_EVENTS.ERROR, error);
      }
    }
  }

  /**
   * Manually trigger token refresh
   */
  async manualRefresh(): Promise<boolean> {
    try {
      await this.refreshToken();
      return true;
    } catch (error) {
      console.error('[TokenRefresh] Manual refresh failed:', error);
      return false;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    try {
      const decoded = jwtDecode<JWTPayload>(token);
      const expiresAt = decoded.exp * 1000;
      const now = Date.now();
      return expiresAt < now;
    } catch (error) {
      console.error('[TokenRefresh] Token validation error:', error);
      return true; // Treat invalid tokens as expired
    }
  }

  /**
   * Get time until token expires
   */
  getTimeUntilExpiry(token: string): number | null {
    try {
      const decoded = jwtDecode<JWTPayload>(token);
      const expiresAt = decoded.exp * 1000;
      const now = Date.now();
      return Math.max(0, expiresAt - now);
    } catch {
      return null;
    }
  }

  /**
   * Emit custom events for token state changes
   */
  private emitEvent(eventName: string, detail?: unknown): void {
    const event = new CustomEvent(eventName, { detail });
    window.dispatchEvent(event);
  }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService();
export default tokenRefreshService;
